/**
 * Turning a form into a policy, and a policy into something readable.
 *
 * Kept out of the component and pure, because this is where a policy editor
 * can quietly do the wrong thing: build an expression that means something
 * other than what the person chose, or describe an existing policy as more
 * permissive than it is. Both are silent, and both are a data leak.
 *
 * The editor deliberately offers a SMALL set of shapes rather than a
 * general expression builder. Somebody who needs `or` across three columns
 * can write JSON; somebody opening a blog wants "everyone can read
 * published posts" and should not have to learn a grammar to say it.
 */

export type PolicyRole = 'anon' | 'authenticated'
export type PolicyOperation = 'select' | 'insert' | 'update' | 'delete'

/** The shapes the form offers, in the order they are most often wanted. */
export type Shape =
  | { kind: 'everyone' }
  | { kind: 'column-true'; column: string }
  | { kind: 'column-equals'; column: string; value: string }
  | { kind: 'owned-by-user'; column: string }
  | { kind: 'custom'; json: string }

export type FormState = {
  table: string
  role: PolicyRole
  operation: PolicyOperation
  name: string
  shape: Shape
}

export type BuildResult =
  | { ok: true; policy: Record<string, unknown> }
  | { ok: false; error: string }

/** Which clause an operation needs. Mirrors the data plane's own rule. */
export const needsUsing = (op: PolicyOperation): boolean => op !== 'insert'
export const needsCheck = (op: PolicyOperation): boolean => op === 'insert' || op === 'update'

export function buildExpression(shape: Shape): { ok: true; expr: unknown } | { ok: false; error: string } {
  switch (shape.kind) {
    case 'everyone':
      return { ok: true, expr: true }
    case 'column-true':
      if (!shape.column) return { ok: false, error: 'Choose a column.' }
      return { ok: true, expr: { col: shape.column, eq: true } }
    case 'column-equals': {
      if (!shape.column) return { ok: false, error: 'Choose a column.' }
      if (shape.value === '') return { ok: false, error: 'Enter a value to compare with.' }
      // Typed the way it reads: "10" on a number column has to bind a
      // number, or it silently matches nothing.
      const value = shape.value === 'true' ? true
        : shape.value === 'false' ? false
        : shape.value.trim() !== '' && Number.isFinite(Number(shape.value)) ? Number(shape.value)
        : shape.value
      return { ok: true, expr: { col: shape.column, eq: value } }
    }
    case 'owned-by-user':
      if (!shape.column) return { ok: false, error: 'Choose the column holding the user id.' }
      return { ok: true, expr: { col: shape.column, eq: { ctx: 'userId' } } }
    case 'custom': {
      if (shape.json.trim() === '') return { ok: false, error: 'Enter an expression, or true.' }
      try {
        return { ok: true, expr: JSON.parse(shape.json) as unknown }
      } catch (err) {
        return { ok: false, error: `Not valid JSON: ${err instanceof Error ? err.message : String(err)}` }
      }
    }
  }
}

export function build(form: FormState): BuildResult {
  if (!form.table) return { ok: false, error: 'Choose a table.' }
  if (!/^[a-z0-9_-]{1,64}$/.test(form.name)) {
    return { ok: false, error: 'A name uses lowercase letters, digits, _ or - (up to 64).' }
  }
  const built = buildExpression(form.shape)
  if (!built.ok) return built

  // The SAME expression fills both clauses when an operation needs both.
  // An update whose `using` and `check` differ is a real thing to want and
  // a rare one; offering two builders here would double the surface for the
  // sake of a case that belongs in the JSON editor.
  const policy: Record<string, unknown> = {
    table: form.table, role: form.role, operation: form.operation, name: form.name,
  }
  if (needsUsing(form.operation)) policy.using = built.expr
  if (needsCheck(form.operation)) policy.check = built.expr
  return { ok: true, policy }
}

/**
 * A policy as a sentence.
 *
 * Read far more often than it is written, and the one thing it must never
 * do is describe a policy as narrower than it is - somebody skimming a list
 * and seeing "published posts only" when the policy says `true` would
 * believe private rows were protected.
 */
export function describe(p: {
  role: string; operation: string; using: unknown; check: unknown
}): string {
  const expr = p.operation === 'insert' ? p.check : p.using
  return `${p.role} may ${p.operation} ${phrase(expr)}`
}

function phrase(expr: unknown): string {
  if (expr === true) return 'every row'
  if (expr === false) return 'no rows'
  if (expr === null || expr === undefined) return 'nothing specified'
  if (typeof expr === 'object') {
    const o = expr as Record<string, unknown>
    if (typeof o.col === 'string') {
      if (o.eq !== undefined) {
        const v = o.eq
        if (typeof v === 'object' && v !== null && (v as { ctx?: string }).ctx === 'userId') {
          return `rows where ${o.col} is the signed-in user`
        }
        return `rows where ${o.col} is ${JSON.stringify(v)}`
      }
      if (o.isNull !== undefined) return `rows where ${o.col} is ${o.isNull ? '' : 'not '}null`
    }
    if (Array.isArray(o.and)) return o.and.map(phrase).join(' and ')
    if (Array.isArray(o.or)) return o.or.map(phrase).join(' or ')
  }
  // Anything this cannot phrase is shown AS the expression. Never guessed
  // at, never summarised into something more permissive than the truth.
  return `rows matching ${JSON.stringify(expr)}`
}

/** A suggested name, so the commonest case needs no thought. */
export const suggestName = (role: string, operation: string): string => `${role}-${operation}`
